import {
  ColumnDef,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Table,
  useReactTable,
} from '@tanstack/react-table'
import { observer } from 'mobx-react-lite'
import * as React from 'react'
import { Data, OrgPersonalSkillsTableRow } from '../data'
import { fuzzyFilter } from 'app/packs/src/utils/table-helpers'
import { SkeletonCell } from 'components/table/skeleton'

type TableContextProps = {
  columnFilters: ColumnFiltersState
  setColumnFilters: (value: ColumnFiltersState) => void
  data: OrgPersonalSkillsTableRow[]
  globalFilter: string
  setGlobalFilter: (value: string) => void
  table: Table<OrgPersonalSkillsTableRow>
  loading: boolean
  selectedSkillId: string | null
  setSelectedSkillId: (id: string | null) => void
}

const TableContext = React.createContext<TableContextProps | null>(null)

export const TableProvider = observer(
  ({
    loading = false,
    children,
    data,
  }: {
    children: React.ReactNode | ((ctx: TableContextProps) => React.ReactNode)
    loading?: boolean
    data: OrgPersonalSkillsTableRow[]
  }) => {
    const [columnFilters, setColumnFilters] =
      React.useState<ColumnFiltersState>([])

    const [globalFilter, setGlobalFilter] = React.useState('')
    const [selectedSkillId, setSelectedSkillId] = React.useState<string | null>(
      null
    )
    const vm = React.useMemo(() => new Data(), [])

    const columns = React.useMemo(() => {
      const cols = vm.columns
      if (!loading) return cols

      return cols.map((col) => {
        if (col.id && ['name', 'actions'].includes(col.id)) return col
        return {
          ...col,
          cell: SkeletonCell,
        } as ColumnDef<OrgPersonalSkillsTableRow, unknown>
      })
    }, [vm, loading])

    const table = useReactTable({
      columns,
      data,
      filterFns: { fuzzy: fuzzyFilter },
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: getSortedRowModel(),
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setGlobalFilter,
      getPaginationRowModel: getPaginationRowModel(),
      initialState: {
        sorting: [{ id: 'name', desc: false }],
        pagination: {
          pageSize: 15,
          pageIndex: 0,
        },
      },
      state: {
        columnFilters,
        globalFilter,
      },
    })

    const contextValue = {
      columnFilters,
      setColumnFilters,
      data,
      globalFilter,
      setGlobalFilter,
      table,
      loading,
      selectedSkillId,
      setSelectedSkillId,
    }

    const content =
      children instanceof Function ? children(contextValue) : children

    return (
      <TableContext.Provider value={contextValue}>
        {content}
      </TableContext.Provider>
    )
  }
)

export const useTable = () => {
  const context = React.useContext(TableContext)

  if (context === null) {
    throw new Error('useTable must be used within a TableProvider')
  }

  return context
}
